/*
 * Ryan O'Dowd
 * 2022-03-08
 *
 * © Copyright 2022 Oakwood Software Consulting, Inc.  All Rights Reserved.
 */
import './index.css';
import 'react-native-reanimated';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
} from 'react-router';
import About from '@containers/About';
import Admin from '@website/containers/Admin';
import App from '@containers/App';
import Assignment from '@containers/Assignment';
import Bibles from '@website/containers/Bibles';
import Discover from '@containers/Discover';
import FeedbackChat from '@containers/FeedbackChat';
import Friends from '@containers/Friends';
import Fundraising from '@containers/Fundraising';
import {
  GestureHandlerRootView,
} from 'react-native-gesture-handler';
import Goal from '@containers/Goal';
import Goals from '@containers/Goals';
import Groups from '@containers/Groups';
import {
  HeaderHeightProvider,
} from '@utils/hooks/headerHeightContext';
import Help from '@containers/Help';
import Home from '@containers/Home';
import {
  KeyboardProvider,
} from 'react-native-keyboard-controller';
import MasqueradeIndicator from './MasqueradeIndicator';
import NewGoal from '@containers/NewGoal';
import Notifications from '@containers/Notifications';
import {
  PersistGate,
} from 'redux-persist/integration/react';
import Practice from '@containers/Practice';
import Profile from '@containers/Profile';
import React from 'react';
import Read from '@containers/Read';
import {
  SafeAreaProvider,
} from 'react-native-safe-area-context';
import School from '@website/containers/School';
import Settings from '@containers/Settings';
import {
  Provider as StoreProvider,
} from 'react-redux';
import ThemeProvider from '@containers/ThemeProvider';
import WebPageStub from '@website/containers/WebPageStub';
import WebsiteArticles from '@website/containers/Articles';
import WebsiteClassroom from '@website/containers/Classroom';
import WebsiteClassroomGoal from '@website/containers/ClassroomGoal';
import WebsiteFundraising from '@website/containers/Fundraising';
import WebsiteI18n from '@website/containers/I18n';
import WebsiteLandingPage from '@website/containers/LandingPage';
// @TODO: import WebsiteMyCampaigns from '@website/containers/MyCampaigns';
// @TODO: import WebsiteOrganization from '@website/containers/Organization';
import WebsiteRoot from '@website/containers/WebsiteRoot';
import WebsiteSchools from '@website/containers/Schools';
import WhatsNew from '@containers/WhatsNew';
import Wrinkles from '@containers/Wrinkles';
import YearInReview from '@containers/YearInReview';
import configureStore from '@utils/store';
import globals from '@utils/globals';
import {
  loadStripe,
} from '@stripe/stripe-js';
/* @TODO:
import {
  createRoot,
} from 'react-dom/client';
*/
// @TODO: import fb from '@utils/firebase';
// @TODO: import hooks from '@hooks';
// @TODO: import reportWebVitals from './reportWebVitals';

const {store, persistor} = configureStore();

//const PrivateRoute = (props: PrivateRouteProps) => { // @TODO: remove need for this...relay on conditional rendering
//  const user = hooks.useTypedSelector((state) => state.oakUser);
//
//  if (!user) {
//    return (
//      <Navigate
//        to={props.unauthenticatedRedirectUrl || '/'}
//        state={{
//          isAuthOpen: true,
//          redirectUrl: location.pathname,
//        }}
//      />
//    );
//  }
//
//  return props.component;
//};
//
//class PrivateRouteProps {
//  component: React.ReactElement;
//
//  unauthenticatedRedirectUrl?: string;
//};

const isAdmin = true; // @TODO: implement

/* @TODO:
const root = createRoot(document.getElementById('root'), {
  onUncaughtError: (error, errorInfo) => {
    fb.log(fb.crashlytics, `error: ${error}, errorInfo: ${errorInfo}`);
    fb.recordError(fb.crashlytics, error);
  },
  onCaughtError: (error, errorInfo) => {
    fb.log(fb.crashlytics, `error: ${error}, errorInfo: ${errorInfo}`);
    fb.recordError(fb.crashlytics, error);
  },
});
root.render(
*/

const stripePromise = loadStripe(globals.stripeKey);

const Setup = () => {
  return (
    <React.StrictMode>{/* @TODO: should this be in native as well? */}
      <GestureHandlerRootView>{/* @TODO: maybe this is better in another file, but it's shared here which is nice */}
        <StoreProvider store={store}>
          <PersistGate
            loading={null}
            persistor={persistor}
          >
            <SafeAreaProvider>
              <BrowserRouter>
                <HeaderHeightProvider>
                  <KeyboardProvider>
                    <ThemeProvider stripePromise={stripePromise}>
                      {(__styles) => (
                        <React.Fragment>
                          <MasqueradeIndicator />
                          <Routes>{/* @TODO: replace these with values exported from actions? */}
                            <Route path='/' element={<WebsiteRoot />}>
                              <Route index element={<WebsiteLandingPage />} />
                              <Route path='/bibles' element={<Bibles />} />
                              <Route path='/fundraising' element={<WebsiteFundraising />} />
                              <Route path='/schools' element={<WebsiteSchools />} />
                              <Route path='/articles' element={<WebsiteArticles />} />
                              <Route path='/i18n/:localeId' element={<WebsiteI18n />} />
                              <Route path='/i18n' element={<Navigate to='/i18n/2' />} />
                              <Route path='/help' element={<WebPageStub><Help /></WebPageStub>} />
                              <Route path='/about' element={<WebPageStub><About /></WebPageStub>} />
                              <Route path='/my-year/:token' element={<WebPageStub><YearInReview isPublic={true} /></WebPageStub>} />
                              {/* @TODO: all schools should be nested routes...top level has outlet and handles auth/redirecting similar to /app */}
                              <Route path='/schools/:schoolId' element={<School />} />{/* @TODO: maybe go back to slugs intead of db ids? */}{/* @TODO: only school admins should be able to see this page */}
                              <Route path='/schools/:schoolId/classrooms/:classroomId' element={<WebsiteClassroom />} />{/* @TODO: should be a protected route */}
                              <Route path='/schools/:schoolId/classrooms/:classroomId/goals/:goalId' element={<WebsiteClassroomGoal />} />{/* @TODO: should be a protected route */}
                              {/* @TODO:
                              <Route path='/organizations/:organizationSlug' element={<WebsiteOrganization />} />
                              <Route path='/campaigns' element={<PrivateRoute component={<WebsiteMyCampaigns />} />}>
                                <Route path=':campaignSlug' element={<WebsiteMyCampaigns />} />
                              </Route>
                              */}

                              {isAdmin && <Route path='/admin' element={<Admin />} />}
                            </Route>

                            <Route path='/app' element={<App />}>
                              <Route index element={<Home />} />
                              {/* @TODO: private route
                              <Route path='/app' element={<PrivateRoute component={<MyCampaigns />} />}>
                              */}
                              <Route path='friends' element={<Friends />} />
                              <Route path='read' element={<Read />} />
                              <Route path='goals' element={<Goals />} />
                              <Route path='goal' element={<Goal />} />
                              <Route path='edit-goal' element={<NewGoal />} />
                              <Route path='settings' element={<Settings />} />
                              <Route path='settings/notifications' element={<Notifications />} />
                              <Route path='wrinkles' element={<Wrinkles />} />
                              <Route path='groups' element={<Groups />} />
                              <Route path='profile' element={<Profile />} />
                              <Route path='learn' element={<Practice />} />
                              <Route path='assignment' element={<Assignment />} />
                              <Route path='discover' element={<Discover />} />
                              <Route path='fundraising' element={<Fundraising />} />
                              <Route path='help' element={<Help />} />
                              <Route path='about' element={<About />} />
                              <Route path='feedback-chat' element={<FeedbackChat />} />
                              <Route path='whats-new' element={<WhatsNew />} />
                              <Route path='year-in-review' element={<YearInReview />} />
                              {/* @TODO:
                              <Route path='*' element={<Error404 />} />
                              */}
                            </Route>

                            <Route path='*' element={<Navigate to='/' />} />
                          </Routes>
                        </React.Fragment>
                      )}
                    </ThemeProvider>
                  </KeyboardProvider>
                </HeaderHeightProvider>
              </BrowserRouter>
            </SafeAreaProvider>
          </PersistGate>
        </StoreProvider>
      </GestureHandlerRootView>
    </React.StrictMode>
  );
};

export default Setup;

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// @TODO: reportWebVitals();
